import { addDays, addMonthsUtc } from '../calendar/calendar-day';
import { INSPECTION_SCHEDULE } from '../inspection-template/inspection-template.interface';
import type { InspectionSchedule } from '../inspection-template/inspection-template.interface';
import { VEHICLE_STATUS } from '../vehicle/vehicle.interface';
import type { Vehicle } from '../vehicle/vehicle.interface';
import type { ScheduledInterval } from './inspection.interface';

/**
 * The calendar step of each interval (SWOOP-391, FR-004), counted from the last completed inspection.
 * `daily` has no step: it falls due every day it was not completed, see {@link fallsDueOn}.
 */
export const INTERVAL_STEPS: Record<Exclude<ScheduledInterval, 'daily'>, { days: number } | { months: number }> = {
  [INSPECTION_SCHEDULE.WEEKLY]: { days: 7 },
  [INSPECTION_SCHEDULE.MONTHLY]: { months: 1 },
  [INSPECTION_SCHEDULE.EVERY_3_MONTHS]: { months: 3 },
  [INSPECTION_SCHEDULE.EVERY_6_MONTHS]: { months: 6 },
  [INSPECTION_SCHEDULE.YEARLY]: { months: 12 },
};

/**
 * The day a vehicle's next inspection on a template falls due (SWOOP-391), or `null` when none does.
 * Pure: `lastCompletedOn` and `today` are calendar days at UTC midnight (see `toCalendarDay`).
 *
 * - On demand never falls due (FR-006).
 * - Daily falls due today unless it was completed today (FR-005).
 * - Never inspected: due today, as soon as the vehicle matches (FR-003).
 * - Otherwise the last completion plus the interval (FR-004), which may be before or after `today`.
 */
export function fallsDueOn(schedule: InspectionSchedule, lastCompletedOn: Date | null, today: Date): Date | null {
  if (schedule === INSPECTION_SCHEDULE.ON_DEMAND) {
    return null;
  }
  if (schedule === INSPECTION_SCHEDULE.DAILY) {
    return lastCompletedOn?.getTime() === today.getTime() ? null : today;
  }
  if (lastCompletedOn == null) {
    return today;
  }
  const step = INTERVAL_STEPS[schedule];
  return 'days' in step ? addDays(lastCompletedOn, step.days) : addMonthsUtc(lastCompletedOn, step.months);
}

/** Whether a fall-due date has been reached: an inspection is never created before its day (FR-010). */
export function isDueBy(dueOn: Date | null, today: Date): boolean {
  return dueOn !== null && dueOn.getTime() <= today.getTime();
}

/**
 * Whether a vehicle counts as fleet for the inspection matcher: live, and neither DRAFT nor ARCHIVED
 * (SWOOP-391, FR-020). The in-memory twin of `VehicleRepository.buildLiveFleetMatch`, which MUST stay in step
 * with it: the due-inspection sync reads a vehicle's pre-write state through this, the card's count through
 * the Mongo filter.
 */
export function isInspectionFleetVehicle(vehicle: Pick<Vehicle, 'deletedAt' | 'status'>): boolean {
  return (
    vehicle.deletedAt == null && vehicle.status !== VEHICLE_STATUS.DRAFT && vehicle.status !== VEHICLE_STATUS.ARCHIVED
  );
}
